import { saveCategoryValue } from '../../../services/resourceLifecycle';
import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Chip,
  Typography,
  TextField,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAttributeCategories, useAttributeOptions } from '../../../contexts/AttributesContext';
import { formatText} from '../../common/TextFormatterProvider';
import AppDatePicker from '../../common/AppDatePicker';
import {
  buildSearchText,
  canonicalizeTagValue,
  getTypedValueLabel,
  toSelectionOption,
  tagValueIdError,
} from '../../../utils/tagValueUtils';

const ADD_NEW_OPTION = { id: '__add_new__', isAddNew: true, name: 'Add New' };

const TagSelector = ({ 
  companyId,
  tableMetadataColumns, 
  selectedTags, 
  onTagsChange 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [addValueDialogOpen, setAddValueDialogOpen] = useState(false);
  const [newValueInput, setNewValueInput] = useState('');
  const [addValueError, setAddValueError] = useState('');
  const [addValueSaving, setAddValueSaving] = useState(false);


  // Extract column names and identify mandatory columns
  const metadataColumnNames = tableMetadataColumns.map(col => col.name);
  
  // The categories offered are the live company list (AttributesProvider)
  // restricted to this table's attribute columns. Keyed on the column names by
  // value, so the intersection is recomputed only when the columns change —
  // not on every render, as the former fetch effect was.
  const { categories: allCategories } = useAttributeCategories();
  const metadataColumnNamesKey = metadataColumnNames.join('|');
  const availableCategories = useMemo(() => {
    const names = metadataColumnNamesKey ? metadataColumnNamesKey.split('|') : [];
    return allCategories.filter((category) => names.includes(category.id));
  }, [allCategories, metadataColumnNamesKey]);
  const mandatoryCategories = useMemo(
    () => tableMetadataColumns.filter(col => col.mandatory === true).map(col => col.name),
    [tableMetadataColumns],
  );

  // The selected category's values, live while the picker is open on it.
  const selectedCategoryType = availableCategories.find(cat => cat.id === selectedCategory)?.type || 'Text';
  const selectedTypeById = useMemo(
    () => (selectedCategory ? { [selectedCategory]: selectedCategoryType } : null),
    [selectedCategory, selectedCategoryType],
  );
  // Absent (not []) until the category's first snapshot: the dialog must not
  // offer "Add New" against a list that is not there yet, or a value that
  // already exists is re-created as a near-duplicate.
  const categoryOptions = useAttributeOptions(selectedCategory || null, selectedTypeById)[selectedCategory];
  const valuesLoading = Boolean(selectedCategory) && categoryOptions === undefined;
  const categoryValues = categoryOptions || [];

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCategoryClose = () => {
    setAnchorEl(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    handleCategoryClose();
    setValueDialogOpen(true);
  };

  const handleValueSelect = () => {
    if (selectedValue) {
      const newTags = selectedTags.filter(tag => tag.category !== selectedCategory);
      newTags.push({
        category: selectedCategory, // sanitised category key
        value: selectedValue.value,
        type: selectedValue.type,
        label: getTypedValueLabel(selectedValue.value, selectedValue.type, selectedValue.name),
      });
      onTagsChange(newTags);
    }
    handleValueDialogClose();
  };

  const handleValueDialogClose = () => {
    setValueDialogOpen(false);
    setSelectedCategory(null);
    setSelectedValue(null);
  };

  const handleOpenAddValueDialog = () => {
    setNewValueInput('');
    setAddValueError('');
    setAddValueDialogOpen(true);
  };

  const handleAddValueDialogClose = () => {
    if (addValueSaving) return;
    setAddValueDialogOpen(false);
    setNewValueInput('');
    setAddValueError('');
  };

  const handleAddNewValue = async () => {
    setAddValueError('');
    try {
      const canonicalValue = canonicalizeTagValue(newValueInput, selectedCategoryType);
      if (canonicalValue === null) {
        setAddValueError('Value cannot be empty');
        return;
      }
      const displayValue = getTypedValueLabel(canonicalValue, selectedCategoryType);
      const idError = tagValueIdError(displayValue);
      if (idError) {
        setAddValueError(idError);
        return;
      }
      const valueId = displayValue.trim().replace(/\s+/g, '_');

      setAddValueSaving(true);
      await saveCategoryValue(companyId, selectedCategory, valueId, {
        name: displayValue,
        value: canonicalValue,
        search_text: buildSearchText(canonicalValue, displayValue),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      const newOption = toSelectionOption({
        name: displayValue,
        value: canonicalValue,
        search_text: buildSearchText(canonicalValue, displayValue),
        type: selectedCategoryType,
      }, valueId);

      // The live values list delivers the new option; select it now.
      setSelectedValue(newOption);
      setAddValueDialogOpen(false);
      setNewValueInput('');
    } catch (err) {
      setAddValueError(err.message || 'Failed to add value');
    } finally {
      setAddValueSaving(false);
    }
  };

  const renderAddValueInput = () => {
    if (selectedCategoryType === 'Date') {
      return (
        <AppDatePicker
          label="Value"
          fullWidth
          size="medium"
          value={newValueInput}
          onChange={(nextDate) => { setNewValueInput(nextDate); setAddValueError(''); }}
          slotProps={{
            textField: {
              autoFocus: true,
              margin: 'dense',
              error: !!addValueError,
              helperText: addValueError,
            },
          }}
        />
      );
    }
    if (selectedCategoryType === 'Number') {
      return (
        <TextField
          autoFocus
          margin="dense"
          label="Value"
          type="number"
          fullWidth
          value={newValueInput || ''}
          onChange={(e) => { setNewValueInput(e.target.value); setAddValueError(''); }}
          error={!!addValueError}
          helperText={addValueError}
          onKeyPress={(e) => { if (e.key === 'Enter') handleAddNewValue(); }}
        />
      );
    }
    if (selectedCategoryType === 'Yes/No') {
      return (
        <FormControl fullWidth margin="dense" error={!!addValueError}>
          <InputLabel>Value</InputLabel>
          <Select
            autoFocus
            value={newValueInput === true ? 'true' : newValueInput === false ? 'false' : ''}
            label="Value"
            onChange={(e) => { setNewValueInput(e.target.value === 'true'); setAddValueError(''); }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
          {addValueError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>{addValueError}</Typography>
          )}
        </FormControl>
      );
    }
    return (
      <TextField
        autoFocus
        margin="dense"
        label="Value"
        fullWidth
        value={newValueInput || ''}
        onChange={(e) => { setNewValueInput(e.target.value); setAddValueError(''); }}
        error={!!addValueError}
        helperText={addValueError}
        onKeyPress={(e) => { if (e.key === 'Enter') handleAddNewValue(); }}
      />
    );
  };

  const handleRemoveTag = (categoryToRemove) => {
    const newTags = selectedTags.filter(tag => tag.category !== categoryToRemove);
    onTagsChange(newTags);
  };

  // Filter out categories that already have values selected
  const availableCategoriesFiltered = availableCategories.filter(
    category => !selectedTags.some(tag => tag.category === category.id)
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {selectedTags.map((tag) => (
          <Chip
            key={tag.category}
            label={`${formatText(tag.category)}${mandatoryCategories.includes(tag.category) ? ' *' : ''}: ${formatText(tag.label || getTypedValueLabel(tag.value, tag.type))}`}
            onDelete={() => handleRemoveTag(tag.category)}
            color={mandatoryCategories.includes(tag.category) ? "primary" : "default"}
            size="small"
          />
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={availableCategoriesFiltered.length === 0}
          size="small"
          variant="outlined"
          sx={{ ml: 'auto' }}
        >
          Add Attribute
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCategoryClose}
      >
        {availableCategoriesFiltered.map((category) => (
          <MenuItem
            key={category.id}
            onClick={() => handleCategorySelect(category.id)}
          >
            {formatText(category.name)} {mandatoryCategories.includes(category.id) && <span style={{ color: 'red', marginLeft: '5px' }}>*</span>}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={valueDialogOpen}
        onClose={handleValueDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Select {formatText(selectedCategory)} Value
          {mandatoryCategories.includes(selectedCategory) && <span style={{ color: 'red' }}> *</span>}
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            sx={{ mt: 1 }}
            disabled={valuesLoading}
            options={valuesLoading ? [] : [ADD_NEW_OPTION, ...categoryValues]}
            getOptionLabel={(option) => (
              option.isAddNew
                ? ''
                : formatText(getTypedValueLabel(option.value, option.type, option.name))
            )}
            value={selectedValue}
            onChange={(event, newValue) => {
              if (newValue?.isAddNew) {
                handleOpenAddValueDialog();
                return;
              }
              setSelectedValue(newValue || null);
            }}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              const addNew = options.find(o => o.isAddNew);
              const rest = options.filter(o => !o.isAddNew);
              const filtered = rest.filter(option => {
                const label = String(option.search_text || option.name || option.id || '').toLowerCase();
                return label.includes(searchTerm);
              });
              return addNew ? [addNew, ...filtered] : filtered;
            }}
            renderOption={(props, option) => {
              if (option.isAddNew) {
                return (
                  <li
                    {...props}
                    key="__add_new__"
                    style={{ ...(props.style || {}), borderBottom: '1px solid rgba(0,0,0,0.12)' }}
                  >
                    <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography color="primary" sx={{ fontWeight: 500 }}>Add New</Typography>
                  </li>
                );
              }
              return (
                <li {...props} key={option.id}>
                  {formatText(getTypedValueLabel(option.value, option.type, option.name))}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Value"
                placeholder={valuesLoading ? 'Loading values…' : 'Type to search...'}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleValueDialogClose}>Cancel</Button>
          <Button
            onClick={handleValueSelect}
            disabled={!selectedValue}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addValueDialogOpen}
        onClose={handleAddValueDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Value to {formatText(selectedCategory)}</DialogTitle>
        <DialogContent>
          {renderAddValueInput()}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            This value will be saved to the category and available for future selections.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddValueDialogClose} disabled={addValueSaving}>Cancel</Button>
          <Button
            onClick={handleAddNewValue}
            variant="contained"
            disabled={addValueSaving}
          >
            {addValueSaving ? 'Saving...' : 'Add Value'}
          </Button>
        </DialogActions>
      </Dialog>

      {mandatoryCategories.length > 0 && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'red' }}>
          * Required attributes
        </Typography>
      )}
    </Box>
  );
};

export default TagSelector;
